/**
 * v2 pipeline for the master-board "publish" — composes existing per-resource
 * steps into the four-phase shape the master sequence page wants.
 *
 * NOT WIRED into the master sequence page yet — that swap is tracked as a
 * follow-up. This builder + the new `master-publish` intent kind let the
 * route-ops utility describe the publish operation today so future migration
 * work has a typed contract and tests to lean on.
 *
 * Branch (custom-branch sequence) updates have no v2 step today; the master
 * board's `useBranchPhase` continues to handle them outside this pipeline.
 */
import type {
  Deps,
  MasterPublishIntent,
  OperationPlan,
  PipelineStep,
} from 'types/modules/routing'

import { makeAssignDailyStep } from '../steps/assignDaily'
import { makeAssignMasterStep } from '../steps/assignMaster'
import { makePreflightStep } from '../steps/preflight'
import { makeSequenceDailyStep } from '../steps/sequenceDaily'
import { makeSequenceMasterStep } from '../steps/sequenceMaster'
import { makeUnassignDailyStep } from '../steps/unassignDaily'
import { makeUnassignMasterStep } from '../steps/unassignMaster'
import { makeVerifyStep } from '../steps/verify'

const STRATEGY_ID = 'v2-pipeline' as const

export const buildMasterPublishPipeline = (
  intent: MasterPublishIntent,
  deps: Deps,
): OperationPlan => {
  const steps: PipelineStep[] = []

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  // ── UNASSIGN ──────────────────────────────────────────────────────────
  if (intent.unassignedDailyWoIds && intent.unassignedDailyWoIds.length > 0) {
    steps.push(
      makeUnassignDailyStep(
        {
          routeId: intent.routeId,
          workOrderIds: intent.unassignedDailyWoIds,
        },
        deps,
      ) as PipelineStep,
    )
  }
  if (intent.unassignedMasterCseIds && intent.unassignedMasterCseIds.length > 0) {
    steps.push(
      makeUnassignMasterStep(
        {
          routeId: intent.routeId,
          dayOfWeek: intent.dayOfWeek,
          cseIds: intent.unassignedMasterCseIds,
          // Default false to mirror master board view today — the FE controls
          // the daily fanout via the resequence phase below.
          futureStops: intent.unassignMasterFutureStops ?? false,
        },
        deps,
      ) as PipelineStep,
    )
  }

  // ── ASSIGN ────────────────────────────────────────────────────────────
  if (intent.assignedMasterCseIds && intent.assignedMasterCseIds.length > 0) {
    steps.push(
      makeAssignMasterStep(
        {
          routeId: intent.routeId,
          dayOfWeek: intent.dayOfWeek,
          cseIds: intent.assignedMasterCseIds,
          // futureStops:false matches what the master board view sends today.
          // Daily occurrences are then reconciled by the resequence step.
          futureStops: false,
        },
        deps,
      ) as PipelineStep,
    )
  }
  if (intent.assignedDailyByDate) {
    for (const { serviceDate, workOrderIds } of intent.assignedDailyByDate) {
      if (workOrderIds.length === 0) continue
      steps.push(
        makeAssignDailyStep(
          {
            routeId: intent.routeId,
            serviceDate,
            workOrderIds,
          },
          deps,
        ) as PipelineStep,
      )
    }
  }

  // ── RESEQUENCE ────────────────────────────────────────────────────────
  if (intent.masterOrderedStops && intent.masterOrderedStops.length > 0) {
    steps.push(
      makeSequenceMasterStep(
        {
          routeId: intent.routeId,
          dayOfWeek: intent.dayOfWeek,
          orderedStops: intent.masterOrderedStops,
        },
        deps,
      ) as PipelineStep,
    )
  }
  if (intent.dailyOrderedByDate) {
    for (const { serviceDate, orderedStops } of intent.dailyOrderedByDate) {
      if (orderedStops.length === 0) continue
      steps.push(
        makeSequenceDailyStep(
          {
            routeId: intent.routeId,
            serviceDate,
            orderedStops,
          },
          deps,
        ) as PipelineStep,
      )
    }
  }

  steps.push(makeVerifyStep({ intent }, deps) as PipelineStep)

  return { strategy: STRATEGY_ID, intent, steps }
}
